import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import { TOPIC_LABELS } from "@/lib/publicApi";
import { Loading } from "@/components/common";
import { EmptyState, MetricCard, PageHeader, Panel, PanelHeader } from "./ui";
import ConversationMetrics from "./ConversationsMetrics";

const TOTAL_LABELS = {
  content_opened: "Aberturas de conteúdos",
  treatment_opened: "Aberturas de tratamentos",
  content_favorited: "Conteúdos favoritados",
  anonymous_questions: "Perguntas anônimas enviadas",
  evaluation_requests: "Solicitações de avaliação",
  published_contents: "Conteúdos publicados",
};

const Bars = ({ rows, testId, labelMap }) => (
  <div data-testid={testId} className="space-y-3">
    {rows.length === 0 ? (
      <p className="text-[14px] text-ink-500">Sem dados suficientes.</p>
    ) : (
      rows.map((row) => {
        const max = Math.max(...rows.map((r) => r.count)) || 1;
        return (
          <div key={row.label}>
            <div className="flex items-baseline justify-between gap-3 text-[14.5px]">
              <span className="text-ink-700">{labelMap?.[row.label] || row.label}</span>
              <span className="font-semibold text-ink-900">{row.count}</span>
            </div>
            <div className="mt-1.5 h-2 rounded-full bg-sand">
              <div className="h-2 rounded-full bg-sage-600" style={{ width: `${(row.count / max) * 100}%` }} />
            </div>
          </div>
        );
      })
    )}
  </div>
);

export default function PortalMetrics() {
  const { api } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data: payload } = await api.get("/portal/metrics");
        setData(payload);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
        setData(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (data === null) return <Loading label="Carregando métricas…" />;
  if (data === false)
    return (
      <EmptyState
        testId="metrics-error"
        icon={BarChart3}
        title="Métricas indisponíveis"
        description="Não foi possível carregar os indicadores agora. Tente novamente em alguns instantes."
      />
    );

  return (
    <div data-testid="portal-metrics-page" className="space-y-7">
      <PageHeader kicker="Produto" title="Métricas agregadas" subtitle={data.privacy_note} />

      <div className="flex flex-wrap gap-2">
        <Link
          to="/portal/metricas/altas"
          data-testid="metrics-discharge-button"
          className="inline-flex h-11 items-center rounded-xl bg-ink-900 px-4 text-[14.5px] font-semibold text-white hover:bg-ink-800"
        >
          Métricas de alta
        </Link>
        <Link
          to="/portal/metricas/pagamentos"
          data-testid="metrics-payment-button"
          className="inline-flex h-11 items-center rounded-xl border border-border bg-white px-4 text-[14.5px] font-semibold text-ink-800 hover:bg-sand"
        >
          Métricas de meios de pagamento
        </Link>
      </div>

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {Object.entries(data.totals).map(([key, value]) => (
          <MetricCard key={key} testId={`metric-total-${key}`} label={TOTAL_LABELS[key] || key} value={value} />
        ))}
      </section>

      <ConversationMetrics />

      <div className="grid gap-5 lg:grid-cols-2">
        <Panel>
          <PanelHeader label="Conteúdo" title="Tratamentos mais consultados" />
          <div className="mt-5">
            <Bars rows={data.top_treatments} testId="metrics-top-treatments" />
          </div>
        </Panel>
        <Panel>
          <PanelHeader label="Busca" title="Temas mais buscados" />
          <div className="mt-5">
            <Bars rows={data.top_search_topics} testId="metrics-top-topics" />
          </div>
        </Panel>
        <Panel>
          <PanelHeader label="Canal anônimo" title="Temas das perguntas" />
          <div className="mt-5">
            {data.question_topics_suppressed ? (
              <p data-testid="metrics-suppressed" className="text-[14px] leading-relaxed text-ink-500">
                Segmentação oculta até atingir o volume mínimo de {data.minimum_segment_volume} registros.
              </p>
            ) : (
              <Bars rows={data.question_topics} testId="metrics-question-topics" labelMap={TOPIC_LABELS} />
            )}
          </div>
        </Panel>
        <Panel>
          <PanelHeader label="Admissões" title="Conversão das solicitações" />
          <div className="mt-5">
            <Bars rows={data.request_funnel} testId="metrics-request-funnel" />
          </div>
        </Panel>
      </div>
    </div>
  );
}
